import React, { useCallback, useState, useRef, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import CharacterCount from '@tiptap/extension-character-count';
import Highlight from '@tiptap/extension-highlight';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import Youtube from '@tiptap/extension-youtube';
import { common, createLowlight } from 'lowlight';
import Icon from '../../components/AppIcon';
import './BlogEditor.css';

const lowlight = createLowlight(common);

// ── Toolbar Button ────────────────────────────────────────────────────
const ToolbarBtn = ({ onClick, isActive, disabled, icon, title, children }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={isActive ? 'is-active' : ''}
        title={title}
    >
        {icon ? <Icon name={icon} size={16} /> : children}
    </button>
);

// ── Link Input Popup ──────────────────────────────────────────────────
const LinkPopup = ({ editor, onClose }) => {
    const [url, setUrl] = useState(editor.getAttributes('link').href || '');
    const inputRef = useRef(null);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (url.trim()) {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url.trim(), target: '_blank' }).run();
        } else {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
        onClose();
    };

    return (
        <div className="link-popup" style={{ top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>
            <form onSubmit={handleSubmit} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                    ref={inputRef}
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com"
                />
                <button type="submit" className="link-confirm">Apply</button>
                <button type="button" className="link-cancel" onClick={onClose}>Cancel</button>
            </form>
        </div>
    );
};

// ── Toolbar ───────────────────────────────────────────────────────────
const EditorToolbar = ({ editor }) => {
    const [showLinkInput, setShowLinkInput] = useState(false);

    if (!editor) return null;

    const addImage = () => {
        const url = window.prompt('Enter image URL:');
        if (url) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    };

    const addYoutube = () => {
        const url = window.prompt('Enter YouTube URL:');
        if (url) {
            editor.commands.setYoutubeVideo({
                src: url,
            });
        }
    };

    const insertTable = () => {
        editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    };

    return (
        <>
            <div className="editor-toolbar">
                {/* Text Style */}
                <div className="toolbar-group">
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                        isActive={editor.isActive('heading', { level: 1 })}
                        title="Heading 1 (Ctrl+Alt+1)"
                    >
                        <span style={{ fontSize: '13px', fontWeight: 800 }}>H1</span>
                    </ToolbarBtn>
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                        isActive={editor.isActive('heading', { level: 2 })}
                        title="Heading 2 (Ctrl+Alt+2)"
                    >
                        <span style={{ fontSize: '12px', fontWeight: 700 }}>H2</span>
                    </ToolbarBtn>
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
                        isActive={editor.isActive('heading', { level: 3 })}
                        title="Heading 3 (Ctrl+Alt+3)"
                    >
                        <span style={{ fontSize: '11px', fontWeight: 700 }}>H3</span>
                    </ToolbarBtn>
                </div>

                <div className="toolbar-divider" />

                {/* Inline formatting */}
                <div className="toolbar-group">
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleBold().run()}
                        isActive={editor.isActive('bold')}
                        icon="Bold"
                        title="Bold (Ctrl+B)"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleItalic().run()}
                        isActive={editor.isActive('italic')}
                        icon="Italic"
                        title="Italic (Ctrl+I)"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleUnderline().run()}
                        isActive={editor.isActive('underline')}
                        icon="Underline"
                        title="Underline (Ctrl+U)"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleStrike().run()}
                        isActive={editor.isActive('strike')}
                        icon="Strikethrough"
                        title="Strikethrough"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleCode().run()}
                        isActive={editor.isActive('code')}
                        icon="Code"
                        title="Inline code"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleHighlight().run()}
                        isActive={editor.isActive('highlight')}
                        icon="Highlighter"
                        title="Highlight"
                    />
                </div>

                <div className="toolbar-divider" />

                {/* Blocks */}
                <div className="toolbar-group">
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleBulletList().run()}
                        isActive={editor.isActive('bulletList')}
                        icon="List"
                        title="Bullet list"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleOrderedList().run()}
                        isActive={editor.isActive('orderedList')}
                        icon="ListOrdered"
                        title="Numbered list"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleTaskList().run()}
                        isActive={editor.isActive('taskList')}
                        icon="ListChecks"
                        title="Task list"
                    />
                </div>

                <div className="toolbar-divider" />

                {/* Rich blocks */}
                <div className="toolbar-group">
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleBlockquote().run()}
                        isActive={editor.isActive('blockquote')}
                        icon="Quote"
                        title="Blockquote"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
                        isActive={editor.isActive('codeBlock')}
                        icon="FileCode"
                        title="Code block"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().setHorizontalRule().run()}
                        icon="Minus"
                        title="Horizontal rule"
                    />
                </div>

                <div className="toolbar-divider" />

                {/* Insert */}
                <div className="toolbar-group">
                    <ToolbarBtn
                        onClick={() => setShowLinkInput(!showLinkInput)}
                        isActive={editor.isActive('link')}
                        icon="Link"
                        title="Insert link"
                    />
                    <ToolbarBtn onClick={addImage} icon="ImagePlus" title="Insert image from URL" />
                    <ToolbarBtn onClick={addYoutube} icon="Youtube" title="Insert YouTube video" />
                    <ToolbarBtn onClick={insertTable} icon="Table" title="Insert table" />
                </div>

                <div className="toolbar-divider" />

                {/* Table controls (show only when inside a table) */}
                {editor.isActive('table') && (
                    <div className="toolbar-group">
                        <ToolbarBtn
                            onClick={() => editor.chain().focus().addColumnAfter().run()}
                            title="Add column"
                        >
                            <span style={{ fontSize: '10px', fontWeight: 700 }}>+Col</span>
                        </ToolbarBtn>
                        <ToolbarBtn
                            onClick={() => editor.chain().focus().addRowAfter().run()}
                            title="Add row"
                        >
                            <span style={{ fontSize: '10px', fontWeight: 700 }}>+Row</span>
                        </ToolbarBtn>
                        <ToolbarBtn
                            onClick={() => editor.chain().focus().deleteColumn().run()}
                            title="Delete column"
                        >
                            <span style={{ fontSize: '10px', fontWeight: 700, color: '#ef4444' }}>-Col</span>
                        </ToolbarBtn>
                        <ToolbarBtn
                            onClick={() => editor.chain().focus().deleteRow().run()}
                            title="Delete row"
                        >
                            <span style={{ fontSize: '10px', fontWeight: 700, color: '#ef4444' }}>-Row</span>
                        </ToolbarBtn>
                        <ToolbarBtn
                            onClick={() => editor.chain().focus().deleteTable().run()}
                            title="Delete table"
                            icon="Trash2"
                        />
                        <div className="toolbar-divider" />
                    </div>
                )}

                {/* Text alignment */}
                <div className="toolbar-group">
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().setTextAlign('left').run()}
                        isActive={editor.isActive({ textAlign: 'left' })}
                        icon="AlignLeft"
                        title="Align left"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().setTextAlign('center').run()}
                        isActive={editor.isActive({ textAlign: 'center' })}
                        icon="AlignCenter"
                        title="Align center"
                    />
                </div>

                <div className="toolbar-divider" />

                {/* Undo / Redo */}
                <div className="toolbar-group">
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().undo().run()}
                        disabled={!editor.can().undo()}
                        icon="Undo2"
                        title="Undo (Ctrl+Z)"
                    />
                    <ToolbarBtn
                        onClick={() => editor.chain().focus().redo().run()}
                        disabled={!editor.can().redo()}
                        icon="Redo2"
                        title="Redo (Ctrl+Shift+Z)"
                    />
                </div>
            </div>

            {showLinkInput && (
                <LinkPopup editor={editor} onClose={() => setShowLinkInput(false)} />
            )}
        </>
    );
};

// ── Main BlogEditor Component ─────────────────────────────────────────
const BlogEditor = ({ content, onChange, placeholder = 'Start writing your technical masterpiece...' }) => {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                codeBlock: false, // We use CodeBlockLowlight instead
                heading: { levels: [1, 2, 3] },
            }),
            Placeholder.configure({ placeholder }),
            Underline,
            Highlight.configure({ multicolor: false }),
            Link.configure({
                openOnClick: false,
                autolink: true,
                defaultProtocol: 'https',
                HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
            }),
            Image.configure({
                inline: false,
                allowBase64: false,
                HTMLAttributes: { loading: 'lazy' },
            }),
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            TaskList,
            TaskItem.configure({ nested: true }),
            HorizontalRule,
            CharacterCount,
            Table.configure({ resizable: false }),
            TableRow,
            TableHeader,
            TableCell,
            CodeBlockLowlight.configure({ lowlight }),
            Youtube.configure({
                controls: true,
                nocookie: true,
                allowFullscreen: true,
                HTMLAttributes: {
                    class: 'tiptap-youtube-embed',
                },
            }),
        ],
        content: content || '',
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange(html);
        },
        editorProps: {
            attributes: {
                class: 'tiptap',
            },
        },
    });

    // Update content when external content changes (e.g., loading a draft)
    useEffect(() => {
        if (editor && content !== undefined) {
            const currentHTML = editor.getHTML();
            // Only update if the content is truly different (avoids cursor jumps)
            if (content !== currentHTML && content !== '<p></p>') {
                editor.commands.setContent(content || '', false);
            }
        }
    }, [content, editor]);

    const charCount = editor?.storage.characterCount.characters() ?? 0;
    const wordCount = editor?.storage.characterCount.words() ?? 0;

    return (
        <div className="blog-editor flex gap-4 flex-1 items-stretch">
            <div className="relative">
                <EditorToolbar editor={editor} />
            </div>
            <div className="flex-1 min-w-0 flex flex-col h-full relative">
                    <EditorContent editor={editor} className="flex-1 flex flex-col [&>div]:flex-1 [&>div]:outline-none" />
            </div>
        </div>
    );
};

export default BlogEditor;
